import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import CityScreen from './CityScreen';
import { CITY_SHOP_RESTRICTED_ITEM_TYPES, ITEM_TYPE_LABELS, getGalleryImageSrc, isWeaponOrAmmoType } from './cityUtils';
import { useCityContext } from './useCityContext';

const CITY_SPECIAL_TYPES = new Set([4, 5, 6]);
const CITY_ACCESSORY_TYPES = new Set([9, 10, 11, 12]);

function toPositiveInt(value, fallback = 1) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function CityShop() {
  const location = useLocation();
  const { loading, error, context, setContext, cityTypeLabel } = useCityContext();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [shopError, setShopError] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const [buyQtyByItem, setBuyQtyByItem] = useState({});
  const [sellQtyByInventory, setSellQtyByInventory] = useState({});
  const [buyCart, setBuyCart] = useState({});
  const [sellCart, setSellCart] = useState({});
  const [descriptionItem, setDescriptionItem] = useState(null);
  const access = context?.access || {};
  const isRpgShop = new URLSearchParams(location.search).get('rpg') === '1';
  const visibleCatalog = useMemo(() => {
    if (isRpgShop) {
      return catalog.filter((item) => CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(item.item_type)));
    }
    return catalog.filter((item) => !CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(item.item_type)));
  }, [catalog, isRpgShop]);
  const visibleInventory = useMemo(() => {
    if (isRpgShop) {
      return inventory.filter((row) => CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(row.item_type)));
    }
    return inventory.filter((row) => !CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(row.item_type)));
  }, [inventory, isRpgShop]);

  async function loadShopData() {
    try {
      const res = await api.get('/robots/my-city-shop/catalog');
      setCatalog(res.data?.catalog || []);
      setInventory(res.data?.inventory || []);
      if (res.data?.bot || res.data?.city) {
        setContext((prev) => ({
          ...prev,
          bot: res.data?.bot || prev?.bot || null,
          city: res.data?.city || prev?.city || null
        }));
      }
      setShopError('');
    } catch (err) {
      setCatalog([]);
      setInventory([]);
      setShopError(err.response?.data?.error || 'Error cargando datos de tienda');
      throw err;
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function bootstrapShop() {
      try {
        await api.post('/robots/my-city-rpg-context/scene', {
          scene_key: isRpgShop ? 'city_shop' : ''
        });
      } catch (_err) {
        // If the RPG scene cannot be updated, we still load the shop.
      }

      if (cancelled) return;
      loadShopData().catch(() => {});
    }

    void bootstrapShop();

    return () => {
      cancelled = true;
      if (isRpgShop) {
        void api.post('/robots/my-city-rpg-context/scene', { scene_key: '' }).catch(() => {});
      }
    };
  }, [isRpgShop]);

  const categories = useMemo(() => {
    const set = new Set(visibleCatalog.map((i) => Number(i.item_type)));
    return ['all', ...Array.from(set).sort((a, b) => a - b)];
  }, [visibleCatalog]);

  const filteredCatalog = useMemo(() => {
    const term = search.trim().toLowerCase();
    return visibleCatalog.filter((item) => {
      const byCategory = category === 'all' || Number(item.item_type) === Number(category);
      if (!byCategory) return false;
      if (!term) return true;
      const name = String(item.name || '').toLowerCase();
      const description = String(item.description || '').toLowerCase();
      return name.includes(term) || description.includes(term);
    });
  }, [visibleCatalog, search, category]);

  const normalItems = filteredCatalog.filter((item) => !CITY_SPECIAL_TYPES.has(Number(item.item_type)));
  const specialItems = filteredCatalog.filter((item) => CITY_SPECIAL_TYPES.has(Number(item.item_type)));

  const inventoryById = useMemo(() => {
    const map = new Map();
    visibleInventory.forEach((row) => map.set(Number(row.id), row));
    return map;
  }, [visibleInventory]);

  const catalogById = useMemo(() => {
    const map = new Map();
    visibleCatalog.forEach((row) => map.set(Number(row.id), row));
    return map;
  }, [visibleCatalog]);

  const buyCartLines = useMemo(() => Object.entries(buyCart).map(([itemId, qty]) => ({ itemId: Number(itemId), quantity: Number(qty) })), [buyCart]);
  const sellCartLines = useMemo(() => Object.entries(sellCart).map(([inventoryId, qty]) => ({ inventoryId: Number(inventoryId), quantity: Number(qty) })), [sellCart]);

  const buyCartTotal = useMemo(() => buyCartLines.reduce((sum, line) => {
    const item = catalogById.get(line.itemId);
    return sum + (item ? Math.max(1, Number(item.value || 0)) * line.quantity : 0);
  }, 0), [buyCartLines, catalogById]);

  const sellCartTotal = useMemo(() => sellCartLines.reduce((sum, line) => {
    const row = inventoryById.get(line.inventoryId);
    return sum + (row ? Math.max(1, Math.floor(Number(row.value || 0) * 0.6)) * line.quantity : 0);
  }, 0), [sellCartLines, inventoryById]);

  const descriptionText = String(descriptionItem?.description || '').trim();
  const descriptionImage = descriptionItem ? getGalleryImageSrc(descriptionItem) : '';

  function addToBuyCart(item) {
    if (access.canBuyWeaponsAmmo === false && isWeaponOrAmmoType(item.item_type)) return;
    const qty = toPositiveInt(buyQtyByItem[item.id], 1);
    setBuyCart((prev) => ({ ...prev, [item.id]: (prev[item.id] || 0) + qty }));
  }

  function addToSellCart(row) {
    if (CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(row.item_type)) && !isRpgShop) return;
    const qty = toPositiveInt(sellQtyByInventory[row.id], 1);
    if (Number(row.equipped) === 1) return;
    const inCart = Number(sellCart[row.id] || 0);

    if (Number(row.item_type) === 2) {
      const clipSize = Math.max(1, Number(row.power || 1));
      const currentShots = Number(row.quantity || 0);
      const maxClips = Math.floor(currentShots / clipSize);
      const availableToAdd = Math.max(0, maxClips - inCart);
      if (qty > availableToAdd) return;
    } else if (qty > Math.max(0, Number(row.quantity || 0) - inCart)) {
      return;
    }

    setSellCart((prev) => ({ ...prev, [row.id]: (prev[row.id] || 0) + qty }));
  }

  function removeBuyLine(itemId) {
    setBuyCart((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }

  function removeSellLine(inventoryId) {
    setSellCart((prev) => {
      const next = { ...prev };
      delete next[inventoryId];
      return next;
    });
  }

  async function confirmBuyCart() {
    if (!buyCartLines.length) return;
    if (access.canBuyWeaponsAmmo === false) {
      const hasRestrictedLines = buyCartLines.some((line) => {
        const item = catalogById.get(Number(line.itemId));
        return item && isWeaponOrAmmoType(item.item_type);
      });
      if (hasRestrictedLines) {
        setMessage('Piloto neutral en ciudad de clan: no puedes comprar armas ni municion.');
        return;
      }
    }
    const hasRpgOnlyLines = buyCartLines.some((line) => {
      const item = catalogById.get(Number(line.itemId));
      return item && CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(item.item_type));
    });
    if (hasRpgOnlyLines) {
      setMessage('Estos recursos no se compran en la tienda; solo se venden en la Cúpula.');
      return;
    }
    try {
      setBusy(true);
      setMessage('');
      const res = await api.post('/robots/my-city-shop/buy', { lines: buyCartLines });
      setMessage(res.data?.message || 'Compra realizada');
      setBuyCart({});
      await loadShopData();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error al comprar');
    } finally {
      setBusy(false);
    }
  }

  async function confirmSellCart() {
    if (!sellCartLines.length) return;
    if (!isRpgShop) {
      const hasRpgOnlyLines = sellCartLines.some((line) => {
        const row = inventoryById.get(Number(line.inventoryId));
        return row && CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(row.item_type));
      });
      if (hasRpgOnlyLines) {
        setMessage('Estos recursos solo se pueden vender en la tienda de la Cúpula.');
        return;
      }
    }
    try {
      setBusy(true);
      setMessage('');
      const res = await api.post('/robots/my-city-shop/sell', { lines: sellCartLines });
      setMessage(res.data?.message || 'Venta realizada');
      setSellCart({});
      await loadShopData();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error al vender');
    } finally {
      setBusy(false);
    }
  }

  function openDescription(item) {
    if (!String(item?.description || '').trim()) return;
    setDescriptionItem(item);
  }

  function closeDescription() {
    setDescriptionItem(null);
  }

  if (loading) return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando tienda...</div>;
  if (error) return <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">{error}</div>;

  return (
    <CityScreen
      context={context}
      cityTypeLabel={cityTypeLabel}
      title={`Tienda - ${context.city?.name || 'Ciudad'}`}
      subtitle="Compra y venta de equipo"
      backLinkLabel={isRpgShop ? 'Volver a la Cúpula' : 'Volver a Ciudad'}
      backLinkTarget={isRpgShop ? '/rpg' : '/city'}
      showDashboardLink={!isRpgShop}
    >
      {shopError && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{shopError}</div>}
      {message && <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-3 text-sm text-cyan-200">{message}</div>}
      <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-3 text-sm text-cyan-200">
        Oro disponible: <span className="font-bold">{Number(context?.bot?.money || 0)}</span>
      </div>
      {access.canBuyWeaponsAmmo === false && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          Restriccion de ciudad: como piloto neutral en ciudad de clan puedes vender y usar taller/transporte, pero no comprar armas ni municion.
        </div>
      )}
      <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-3 text-sm text-violet-200">
        {isRpgShop
          ? 'Cúpula activa: los recursos de cultivo y materias primas se pueden vender aquí.'
          : 'Los recursos de cultivo y materias primas solo se pueden vender en la tienda de la Cúpula (RPG).'}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="font-bold text-cyan-300 mb-3">Catálogo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar artículo..."
              className="md:col-span-2 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'Todas las categorías' : (ITEM_TYPE_LABELS[Number(cat)] || `Tipo ${cat}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
            {filteredCatalog.length === 0 && <p className="text-slate-400 text-sm">No hay objetos disponibles con los filtros actuales.</p>}

            {normalItems.length > 0 && <p className="text-xs font-bold text-cyan-300/90 uppercase tracking-wide mt-1">Artículos normales</p>}
            {[...normalItems, ...specialItems].map((item) => {
              const buyRestricted = access.canBuyWeaponsAmmo === false && isWeaponOrAmmoType(item.item_type);
              const hasDescription = Boolean(String(item.description || '').trim());
              const isAccessory = CITY_ACCESSORY_TYPES.has(Number(item.item_type));
              return (
              <div key={item.id} className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${CITY_SPECIAL_TYPES.has(Number(item.item_type)) ? 'border-amber-500/40 bg-[#060c18]' : 'border-slate-700 bg-[#060c18]'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
                    {getGalleryImageSrc(item) ? (
                      <img src={getGalleryImageSrc(item)} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] text-slate-500">No img</span>
                    )}
                  </div>
                  <div>
                    <p className="text-cyan-200 font-bold">{item.name}</p>
                    <p className="text-xs text-slate-400">{ITEM_TYPE_LABELS[Number(item.item_type)] || 'Objeto'} | Nivel {item.level_required} | Oro {item.value}</p>
                    {isAccessory && (
                      <p className="text-[11px] text-cyan-300">Accesorio de hangar: se equipa o se vende desde la tienda.</p>
                    )}
                    {CITY_SPECIAL_TYPES.has(Number(item.item_type)) && (
                      <p className="text-[11px] text-amber-300">Stock especial: {Number(item.special_stock || 0)}</p>
                    )}
                    {buyRestricted && (
                      <p className="text-[11px] text-amber-300">Compra bloqueada para neutral en ciudad de clan.</p>
                    )}
                    {hasDescription && (
                      <button
                        type="button"
                        onClick={() => openDescription(item)}
                        className="mt-2 inline-flex items-center rounded-md border border-cyan-400/30 bg-cyan-500/10 px-2 py-1 text-[11px] font-semibold text-cyan-200 hover:bg-cyan-500/20"
                      >
                        Ver descripción
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    value={buyQtyByItem[item.id] ?? 1}
                    onChange={(e) => setBuyQtyByItem((prev) => ({ ...prev, [item.id]: toPositiveInt(e.target.value, 1) }))}
                    className="w-16 bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                  />
                  <button
                    disabled={busy || buyRestricted}
                    onClick={() => addToBuyCart(item)}
                    className="px-3 py-1 rounded-lg border border-emerald-400/50 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-60"
                  >
                    Añadir
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
            <p className="text-sm text-emerald-200 font-bold">Carrito de compra</p>
            {buyCartLines.length === 0 ? <p className="text-xs text-slate-300">Vacío</p> : buyCartLines.map((line) => {
              const item = catalogById.get(line.itemId);
              return (
                <div key={line.itemId} className="flex items-center justify-between text-xs text-slate-200 mt-1">
                  <span>{item?.name || `#${line.itemId}`} x{line.quantity}</span>
                  <button onClick={() => removeBuyLine(line.itemId)} className="text-rose-300">Quitar</button>
                </div>
              );
            })}
            <p className="text-sm text-cyan-200 mt-2">Total: {buyCartTotal} oro</p>
            <button disabled={busy || buyCartLines.length === 0} onClick={confirmBuyCart} className="mt-2 px-3 py-1 rounded border border-emerald-400/50 text-emerald-200 disabled:opacity-50">Confirmar compra</button>
          </div>
        </section>

        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="font-bold text-cyan-300 mb-3">Venta desde inventario</h2>
          <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
            {visibleInventory.length === 0 && (
              <p className="text-slate-400 text-sm">
                {isRpgShop ? 'No tienes recursos de la Cúpula en el inventario.' : 'Tu inventario no tiene objetos vendibles aquí.'}
              </p>
            )}
            {visibleInventory.map((row) => {
              const isAmmo = Number(row.item_type) === 2;
              const isAccessory = CITY_ACCESSORY_TYPES.has(Number(row.item_type));
              const isRpgOnlyResource = CITY_SHOP_RESTRICTED_ITEM_TYPES.has(Number(row.item_type));
              const clipSize = Math.max(1, Number(row.power || 1));
              const shots = Number(row.quantity || 0);
              const looseShots = isAmmo ? (shots % clipSize) : 0;
              const maxSellUnitsRaw = isAmmo ? Math.floor(shots / clipSize) : Number(row.quantity || 0);
              const inCart = Number(sellCart[row.id] || 0);
              const availableToAdd = Math.max(0, maxSellUnitsRaw - inCart);
              const requested = toPositiveInt(sellQtyByInventory[row.id], 1);
              const cannotAdd = Number(row.equipped) === 1 || availableToAdd <= 0 || requested > availableToAdd || (isRpgOnlyResource && !isRpgShop);

              return (
                <div key={row.id} className="rounded-lg border border-slate-700 bg-[#060c18] p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
                      {getGalleryImageSrc(row) ? (
                        <img src={getGalleryImageSrc(row)} alt={row.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[10px] text-slate-500">No img</span>
                      )}
                    </div>
                    <div>
                      <p className="text-cyan-200 font-bold">{row.name} x{row.quantity}</p>
                      <p className="text-xs text-slate-400">Valor base {row.value} | Venta {Math.max(1, Math.floor(Number(row.value || 0) * 0.6))} | {row.equipped ? 'Equipado' : 'Mochila'}</p>
                      {isAccessory && <p className="text-[11px] text-cyan-300">Accesorio del hangar: se vende o se deja activo desde el hangar.</p>}
                      {isAmmo && <p className="text-[11px] text-slate-300">Cargadores completos: {maxSellUnitsRaw} (tamaño {clipSize})</p>}
                      {!isAmmo && <p className="text-[11px] text-slate-300">Disponible para añadir: {availableToAdd}</p>}
                      {isAmmo && <p className="text-[11px] text-slate-300">Disponible para añadir: {availableToAdd} cargador(es)</p>}
                      {isAmmo && looseShots > 0 && <p className="text-[11px] text-amber-300">Balas sueltas no vendibles: {looseShots}</p>}
                      {isRpgOnlyResource && (
                        <p className="text-[11px] text-violet-300">
                          {isRpgShop ? 'Recurso de Cúpula: puedes venderlo aquí.' : 'Recurso de Cúpula: solo se vende en la tienda RPG.'}
                        </p>
                      )}
                      {CITY_SPECIAL_TYPES.has(Number(row.item_type)) && (
                        <p className="text-[11px] text-amber-300">Recurso especial: al vender aquí, aumentas stock local.</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={Math.max(1, availableToAdd)}
                      value={sellQtyByInventory[row.id] ?? 1}
                      onChange={(e) => setSellQtyByInventory((prev) => ({ ...prev, [row.id]: toPositiveInt(e.target.value, 1) }))}
                      className="w-16 bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                    />
                    <button
                      disabled={busy || cannotAdd}
                      onClick={() => addToSellCart(row)}
                      className="px-3 py-1 rounded-lg border border-amber-400/50 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 disabled:opacity-40"
                    >
                      Añadir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
            <p className="text-sm text-amber-200 font-bold">Carrito de venta</p>
            {sellCartLines.length === 0 ? <p className="text-xs text-slate-300">Vacío</p> : sellCartLines.map((line) => {
              const row = inventoryById.get(line.inventoryId);
              return (
                <div key={line.inventoryId} className="flex items-center justify-between text-xs text-slate-200 mt-1">
                  <span>{row?.name || `#${line.inventoryId}`} x{line.quantity}</span>
                  <button onClick={() => removeSellLine(line.inventoryId)} className="text-rose-300">Quitar</button>
                </div>
              );
            })}
            <p className="text-sm text-cyan-200 mt-2">Total estimado: {sellCartTotal} oro</p>
            <button disabled={busy || sellCartLines.length === 0} onClick={confirmSellCart} className="mt-2 px-3 py-1 rounded border border-amber-400/50 text-amber-200 disabled:opacity-50">Confirmar venta</button>
          </div>
        </section>
      </div>

      {descriptionItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-cyan-500/30 bg-[#08101f] shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-cyan-500/15 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/80">Descripción del artículo</p>
                <h3 className="mt-1 text-xl font-bold text-cyan-100">{descriptionItem.name}</h3>
              </div>
              <button
                type="button"
                onClick={closeDescription}
                className="rounded-full border border-slate-700 bg-black/50 px-3 py-1 text-sm text-slate-200 hover:bg-slate-800"
                aria-label="Cerrar descripción"
              >
                Cerrar
              </button>
            </div>

            <div className="grid gap-4 px-5 py-5 sm:grid-cols-[120px_1fr]">
              <div className="flex items-center justify-center rounded-2xl border border-slate-700 bg-slate-950/70 p-3">
                {descriptionImage ? (
                  <img src={descriptionImage} alt={descriptionItem.name} className="h-24 w-24 object-cover" />
                ) : (
                  <span className="text-xs text-slate-500">Sin imagen</span>
                )}
              </div>

              <div className="space-y-3">
                <div className="rounded-xl border border-slate-700 bg-[#060c18] px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Ficha</p>
                  <p className="mt-1 text-sm text-slate-200">
                    {ITEM_TYPE_LABELS[Number(descriptionItem.item_type)] || 'Objeto'} | Nivel {descriptionItem.level_required} | Oro {descriptionItem.value}
                  </p>
                </div>

                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 text-sm leading-relaxed text-cyan-50">
                  {descriptionText}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </CityScreen>
  );
}
